import type { GeneratedInsight } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { Status } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useIntervalEffect } from "@react-hookz/web";
import React from "react";
import { useStore } from "zustand";

import { getCdbApiClient } from "../../api/cdbApi/cdbApi";
import { notificationStore, NotificationType } from "../../store/notification";
import { getGeneratedInsightNotification } from "../../utils/generatedInsight.utils";
import { getAdvertiserId } from "../../utils/permission.utils";

export const useGetGeneratedInsightsNotifications = (
    generatedInsights: GeneratedInsight[],
): void => {
    const [notifiedInsights, setNotifiedInsights] = React.useState<string[]>(
        [],
    );
    const [upsertNotification, findNotificationByIdentifer] = useStore(
        notificationStore,
        (state) => [
            state.upsertNotification,
            state.findNotificationByIdentifer,
        ],
    );

    React.useEffect(() => {
        generatedInsights.forEach((insight) => {
            if (
                !notifiedInsights.includes(insight.identifier!) &&
                !findNotificationByIdentifer(insight.identifier!)
            ) {
                upsertNotification(
                    getGeneratedInsightNotification(insight),
                    NotificationType.LOADING,
                    insight.identifier,
                );
                setNotifiedInsights((prev) => [...prev, insight.identifier!]);
            }
        });
    }, [generatedInsights, notifiedInsights]);

    const updateGeneratedInsightNotification = React.useCallback(
        async (insight: GeneratedInsight) => {
            const { data: updatedInsight } = await (
                await getCdbApiClient()
            ).getGeneratedInsight({
                advertiserId: getAdvertiserId(),
                executionId: insight.identifier!,
            });
            if (!updatedInsight) {
                return;
            } else if (updatedInsight.status === Status.IN_PROGRESS) {
                upsertNotification(
                    getGeneratedInsightNotification(updatedInsight),
                    NotificationType.LOADING,
                    updatedInsight.identifier,
                );
            } else if (updatedInsight.status === Status.SUCCESS) {
                upsertNotification(
                    getGeneratedInsightNotification(updatedInsight),
                    NotificationType.SUCCESS,
                    updatedInsight.identifier,
                );
            } else if (updatedInsight.status === Status.FAIL) {
                upsertNotification(
                    getGeneratedInsightNotification(updatedInsight),
                    NotificationType.ERROR,
                    updatedInsight.identifier,
                );
            }
        },
        [],
    );

    useIntervalEffect(() => {
        generatedInsights.forEach((insight) => {
            if (
                insight.status === Status.IN_PROGRESS &&
                findNotificationByIdentifer(insight.identifier!) &&
                findNotificationByIdentifer(insight.identifier!)?.type ===
                    NotificationType.LOADING
            ) {
                void updateGeneratedInsightNotification(insight);
            }
        });
    }, 10000);
};
